import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';

const PAD = 8;

function findTarget(selector) {
  const els = Array.from(document.querySelectorAll(selector));
  return els.find(el => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth;
  }) || null;
}

function useTargetRect(selector, active) {
  const [rect, setRect] = useState(null);
  const [searching, setSearching] = useState(true);

  useEffect(() => {
    if (!active) return undefined;
    setRect(null);
    setSearching(true);
    let el = null;
    let tries = 0;
    const measure = () => el && setRect(el.getBoundingClientRect());
    const poll = setInterval(() => {
      el = findTarget(selector);
      tries += 1;
      if (el) {
        clearInterval(poll);
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => { measure(); setSearching(false); }, 350);
      } else if (tries > 25) {
        clearInterval(poll);
        setSearching(false);
      }
    }, 200);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(poll);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [selector, active]);

  return { rect, searching };
}

function popoverStyle(rect) {
  const mobile = window.innerWidth < 640;
  if (mobile || !rect) {
    return mobile
      ? { left: 12, right: 12, bottom: 16 }
      : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 360 };
  }
  const width = 340;
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
  const below = rect.bottom + PAD + 12;
  if (below + 190 < window.innerHeight) return { left, top: below, width };
  return { left, top: Math.max(12, rect.top - PAD - 12 - 190), width };
}

export function TourOverlay({ tour, step, onStep, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = tour.steps[step];
  const onRoute = location.pathname === current.route;
  const { rect, searching } = useTargetRect(current.target, onRoute);
  const last = step === tour.steps.length - 1;

  useEffect(() => {
    if (!onRoute) navigate(current.route);
  }, [onRoute, current.route, navigate]);

  const next = useCallback(() => (last ? onClose(true) : onStep(step + 1)), [last, onClose, onStep, step]);
  const back = useCallback(() => step > 0 && onStep(step - 1), [onStep, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose(false);
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      <div className="absolute inset-0" onClick={e => e.stopPropagation()} />
      {rect ? (
        <div
          className="absolute rounded-lg ring-4 ring-red-500 pointer-events-none transition-all duration-300"
          style={{
            left: rect.left - PAD, top: rect.top - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/60 transition-opacity" />
      )}
      <div
        className="absolute bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 animate-in fade-in zoom-in-95 duration-200"
        style={popoverStyle(rect)}
        role="dialog"
        aria-live="polite"
        data-testid="tour-popover"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 text-[11px] font-ibm font-semibold uppercase tracking-wider text-red-600">
            <GraduationCap className="h-3.5 w-3.5" />
            <span data-testid="tour-progress">{tour.title} · {step + 1} av {tour.steps.length}</span>
          </div>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-slate-700" data-testid="tour-close-btn" aria-label="Avsluta guiden">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-chivo font-bold text-lg text-navy dark:text-white leading-tight" data-testid="tour-step-title">{current.title}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm mt-1.5 leading-relaxed">{current.body}</p>
        {!rect && !searching && onRoute && (
          <p className="text-xs text-slate-400 font-ibm mt-2" data-testid="tour-target-missing">
            Den här delen syns inte just nu på din skärm – fortsätt till nästa steg.
          </p>
        )}
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1">
            {tour.steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-red-500' : 'w-1.5 bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={back} disabled={step === 0} data-testid="tour-back-btn">
              <ChevronLeft className="h-4 w-4 mr-1" />Tillbaka
            </Button>
            <Button size="sm" onClick={next} className="bg-navy hover:bg-navy/90" data-testid="tour-next-btn">
              {last ? 'Klar' : 'Nästa'}{!last && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
